/**
 * P0-20: useGameTick —— 基于 useSyncExternalStore 的每 tick 精确订阅。
 *
 * 取代旧实现"整个左面板每 tick setState"（js/jsx/left.jsx.js:946-948）：
 * 只有 selector 结果（经 equalityFn 判定）发生变化时，组件才重渲染。
 *
 * 用法：const ticks = useGameTick((g) => g?.ticks ?? null);
 */

import { useCallback, useRef, useSyncExternalStore } from "react";
import { getGame, subscribeTick } from "./GameBridge";
import type { GamePage } from "./GameBridge";

export function useGameTick<T>(
  selector: (game: GamePage | null) => T,
  equalityFn: (a: T, b: T) => boolean = Object.is,
): T {
  const cacheRef = useRef<{ has: boolean; value: T }>({
    has: false,
    value: undefined as T,
  });

  // getSnapshot 必须返回缓存值直到结果真正变化，否则会触发无限重渲染。
  // selector/equalityFn 作为依赖闭包捕获：内联箭头传入时 getSnapshot 身份随渲染变化，
  // React 会立即重校验快照——因返回值被缓存，不会产生额外重渲染；
  // 而 subscribe（subscribeTick）身份稳定，不会重新订阅。
  const getSnapshot = useCallback((): T => {
    const next = selector(getGame());
    const cache = cacheRef.current;
    if (cache.has && equalityFn(cache.value, next)) {
      return cache.value;
    }
    cacheRef.current = { has: true, value: next };
    return next;
  }, [selector, equalityFn]);

  return useSyncExternalStore(subscribeTick, getSnapshot, getSnapshot);
}
